import { ref, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';

/**
 * Normaliza el valor del logo (puede venir como string o objeto { downloadURL, gsUrl }) a una sola URL string.
 * Prioriza downloadURL (https) sobre gsUrl para evitar conversión innecesaria.
 */
export function normalizeLogoUrl(logo) {
  if (!logo) return null;
  if (typeof logo === 'string' && logo.trim()) return logo.trim();
  if (typeof logo === 'object') {
    const url = logo.downloadURL || logo.downloadUrl || logo.download_url || logo.gsUrl || logo.gs_url;
    if (typeof url === 'string' && url.trim()) return url.trim();
  }
  return null;
}

/**
 * Convierte una URL de Firebase Storage (gs://) a una URL pública de descarga (https://)
 * @param {string} gsUrl - URL de Firebase Storage (gs://bucket/path)
 * @returns {Promise<string>} URL pública de descarga
 */
export const convertGsUrlToDownloadUrl = async (gsUrl) => {
  if (!gsUrl || typeof gsUrl !== 'string') {
    return null;
  }

  // Si ya es una URL https, devolverla tal como está
  if (gsUrl.startsWith('https://')) {
    return gsUrl;
  }

  // Si es una URL gs://, extraer path dentro del bucket. ref() solo acepta path relativo, NO gs:// completo.
  // Ej: gs://huamachuco-c0d9f.firebasestorage.app/logos/empresas/auto-xxx.png → path = logos/empresas/auto-xxx.png
  if (gsUrl.startsWith('gs://')) {
    try {
      const full = gsUrl.replace('gs://', '').trim();
      const slashIdx = full.indexOf('/');
      const pathInBucket = slashIdx >= 0 ? full.substring(slashIdx + 1).replace(/^\//, '').trim() : full;
      if (!pathInBucket) return null;
      const storageRef = ref(storage, pathInBucket);
      return await getDownloadURL(storageRef);
    } catch (error) {
      console.error('Error convirtiendo gs:// a URL de descarga:', gsUrl, error);
      return null;
    }
  }

  // Si es una ruta relativa de Storage (ej. logos/empresas/auto-xxx.png), usar ref directo
  if (typeof gsUrl === 'string' && gsUrl.trim() && !gsUrl.includes('://')) {
    try {
      const path = gsUrl.trim().replace(/^\//, '');
      if (!path) return null;
      const storageRef = ref(storage, path);
      return await getDownloadURL(storageRef);
    } catch (error) {
      console.error('Error obteniendo URL desde path de Storage:', gsUrl, error);
      return null;
    }
  }

  return null;
};

/**
 * Función helper para obtener la URL de imagen correcta
 * @param {string} imageUrl - URL de la imagen (puede ser gs:// o https://)
 * @returns {Promise<string|null>} URL pública de descarga o null si hay error
 */
export const getImageUrl = async (imageUrl) => {
  if (!imageUrl) return null;
  
  try {
    const downloadUrl = await convertGsUrlToDownloadUrl(imageUrl);
    return downloadUrl;
  } catch (error) {
    console.error('Error obteniendo URL de imagen:', error);
    return null;
  }
};

/**
 * Función para convertir múltiples URLs de imagen en paralelo
 * @param {Array<string>} imageUrls - Array de URLs de imagen
 * @returns {Promise<Array<string|null>>} Array de URLs convertidas
 */
export const convertMultipleImageUrls = async (imageUrls) => {
  if (!Array.isArray(imageUrls)) return [];
  
  try {
    const convertedUrls = await Promise.all(
      imageUrls.map(url => convertGsUrlToDownloadUrl(url))
    );
    return convertedUrls;
  } catch (error) {
    console.error('Error convirtiendo múltiples URLs:', error);
    return imageUrls.map(() => null);
  }
};
